/** @jsx jsx */
import {ChangeEvent, Fragment, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {jsx} from 'theme-ui'
import CustomSelect, {ISelectOption} from './CustomSelect'
import {UdhiyaMetadata} from './shared'

const udhiyaList: UdhiyaMetadata[] = [
  {
    type: 'Udhiya',
    country: 'Morocco',
    unit_price: 330,
    quantity: 1,
    sacrifice: 'Sheep',
  },
  {
    type: 'Udhiya',
    country: 'Cameroun',
    unit_price: 190,
    quantity: 1,
    sacrifice: 'Sheep',
  },
  {
    type: 'Udhiya',
    country: 'Uganda',
    unit_price: 100,
    quantity: 1,
    sacrifice: 'Sheep',
  },
  {
    type: 'Udhiya',
    country: 'Nepal',
    unit_price: 60,
    quantity: 1,
    sacrifice: 'Goat',
  },
  {
    type: 'Udhiya',
    country: 'Madagascar',
    unit_price: 515,
    quantity: 1,
    sacrifice: 'Cow (Zebu)',
  },
]

interface IUdhiya {
  setMetadata: (metadata: UdhiyaMetadata) => void
  onAmountChange: (
    amount: number | null,
    userInput: null | 'custom' | 'fixed',
  ) => void
}

const Udhiya: React.FC<IUdhiya> = ({setMetadata, onAmountChange}) => {
  const {t} = useTranslation()
  const [name, setName] = useState<string>('')
  const [arabicName, setArabicName] = useState<string>('')
  const [itemIndex, setItemIndex] = useState<number>(0)

  const handleChangeType = (item: ISelectOption) => {
    setItemIndex(parseInt(item.value))
  }

  const handleChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.currentTarget.value)
  }

  const handleChangeArabicInput = (event: ChangeEvent<HTMLInputElement>) => {
    setArabicName(event.currentTarget.value)
  }

  const selectUdhiyaList = useMemo(
    () =>
      udhiyaList.map((item, index) => {
        const label = (
          <Fragment>
            {t(item.country).toUpperCase()} - {t(item.sacrifice)}
            {item.extra && ` - ${t(item.extra)}`}
            <strong> - ${item.unit_price} USD </strong>
            {item.disabled && (
              <span
                sx={{
                  fontSize: '10px',
                  bg: 'primary',
                  color: 'white',
                }}
              >
                {t('Sold Out')}
              </span>
            )}
          </Fragment>
        )
        return {
          value: `${index}`,
          selectedLabel: label,
          label: (
            <div>
              {label}
              {item.subtitle && (
                <div
                  sx={{
                    fontSize: 11,
                  }}
                >
                  {t(item.subtitle)}
                </div>
              )}
            </div>
          ),
          isDisabled: item.disabled === true,
        }
      }),
    [t],
  )

  useEffect(() => {
    const metadata: UdhiyaMetadata = udhiyaList[itemIndex]
    setMetadata({...metadata, name, arabic_name: arabicName || undefined})
    onAmountChange(metadata.unit_price, 'fixed')
  }, [itemIndex, name, arabicName, setMetadata, onAmountChange])

  return (
    <div
      sx={{
        py: 16,
        px: 13,
      }}
    >
      <CustomSelect
        value={selectUdhiyaList[itemIndex]}
        onChange={handleChangeType}
        options={selectUdhiyaList}
      />
      <input
        type="text"
        sx={{
          mt: 14,
          width: '100%',
          height: '100%',
          border: '1px solid #DEE3E3',
          borderRadius: 4,
          padding: '15px 12px',
          fontSize: 13,
          outline: 0,
          '::placeholder': {
            color: '#717A7B',
          },
          '&:focus': {
            border: '1px solid #055064',
          },
        }}
        required
        placeholder={
          t('Full name for whom the udhiya will be made') ||
          'Full name for whom the udhiya will be made'
        }
        name="name"
        value={name}
        onChange={handleChangeInput}
      />
      <input
        type="text"
        dir="auto"
        lang="ar"
        sx={{
          mt: 14,
          width: '100%',
          height: '100%',
          border: '1px solid #DEE3E3',
          borderRadius: 4,
          padding: '15px 12px',
          fontSize: 13,
          outline: 0,
          '::placeholder': {
            color: '#717A7B',
          },
          '&:focus': {
            border: '1px solid #055064',
          },
        }}
        placeholder={
          t('Full name in Arabic (optional)') ||
          'Full name in Arabic (optional)'
        }
        name="arabic_name"
        value={arabicName}
        onChange={handleChangeArabicInput}
      />
    </div>
  )
}

export default Udhiya
